import useReexecuteRun, { RerunRequest } from '@/hooks/api/runs/rerun/useReexecuteRun.ts';
import { useCallback } from 'react';
import { ExtractEntityRerunOptions } from '@/types/runs.ts';

type useRerunStepProps = {
  runId: string;
  stepPath: string;
  ignoreCache: boolean;
  feedback: Record<string, string>;
  runDownstream: boolean;
  childrenToRerun?: 'all' | 'failed'; // Optional, defaults to 'all'
  extractEntitiesToRerun?: ExtractEntityRerunOptions; // Optional, default to 'all'
  rerunContext?: RerunRequest['rerun_context'];
};

const makeExecutionRequest = ({
  stepPath,
  ignoreCache,
  feedback,
  runDownstream,
  childrenToRerun,
  extractEntitiesToRerun,
  rerunContext,
}: useRerunStepProps): RerunRequest => ({
  config: {
    ignore_cache: ignoreCache,
    run_downstream: runDownstream,
    rerun_config: {
      step_path: stepPath,
      children_to_rerun: childrenToRerun || 'all',
      extract_entities_to_run: extractEntitiesToRerun || 'all',
    },
  },

  step_feedback: feedback,
  rerun_context: rerunContext,
});

const useRerunStep = () => {
  const { mutate: innerMutate, ...props } = useReexecuteRun();

  const rerunStep = useCallback(
    (rerunProps: useRerunStepProps) => {
      const rerunRequest = makeExecutionRequest({ ...rerunProps });
      return innerMutate({ runId: rerunProps.runId, rerunRequest });
    },
    [innerMutate],
  );

  return { mutate: rerunStep, ...props };
};

export default useRerunStep;
